import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import PortalHeader from "@/components/PortalHeader";
import ApplicationWizard from "@/components/ApplicationWizard";
import ApplicationStatus from "@/components/ApplicationStatus";
import { Lock } from "lucide-react";

export default function ApplicationPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/me/application");
      setApplication(data.application);
      setSignatureStatus(data.signature_status || null);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Stage 3 is not yet unlocked."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = (app) => setApplication(app);
  const onSigned = (resp) => {
    if (resp?.application) setApplication(resp.application);
    if (resp?.signature_status) setSignatureStatus(resp.signature_status);
  };

  const editable = useMemo(() => {
    if (!application) return false;
    return application.status === "draft" || application.status === "more_info_requested";
  }, [application]);

  return (
    <div className="min-h-screen">
      <PortalHeader user={user} onLogout={logout} stage={3} />

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-testid="application-hero">
        <div className="bloom-divider mb-6">✦ Stage Three · Application ✦</div>
        <h1 className="font-serif text-4xl md:text-6xl leading-[0.95] max-w-3xl">
          The <em>Founding Grower</em> Application.
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl">
          Tell us about your farm, your craft, and the bloom future you wish to grow with us.
          Save and return at any time. Only press <em>submit</em> when you are ready.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
          <span>7 sections · ~15 minutes</span>
          <span>·</span>
          <span>Confidential</span>
          <span>·</span>
          <span>Reviewed within 7 business days</span>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-20" data-testid="application-section">
        {loading ? (
          <div className="text-center font-serif italic text-[var(--bloom-ink-muted)] py-24">Loading your application…</div>
        ) : error ? (
          <div className="bloom-card p-10 text-center" data-testid="application-locked">
            <Lock className="mx-auto mb-3 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl mb-2">Stage 3 Locked</h3>
            <p className="text-sm text-[var(--bloom-ink-soft)] max-w-md mx-auto mb-6">{error}</p>
            <button onClick={() => navigate("/portal")} className="bloom-btn bloom-btn-outline" data-testid="back-to-portal-btn">
              Return to Prospectus
            </button>
          </div>
        ) : !editable ? (
          <ApplicationStatus
            application={application}
            signatureStatus={signatureStatus}
            onSigned={onSigned}
            onReopen={load}
          />
        ) : (
          <ApplicationWizard application={application} onSaved={onSaved} onSubmitted={load} />
        )}
      </section>
    </div>
  );
}
